import React, { useEffect, useState } from 'react';
import { api, money } from '../../lib/api';
import { SectionHeader } from '../../components/UI';
import { DataSourceBadge } from '../../components/sales/DataSourceBadge';
import { MonthStatusBadge } from '../../components/sales/MonthStatusBadge';
import { TimeSeriesTile, ClosedSplitTimeSeriesTile } from '../../components/sales/TimeSeriesTile';
import { StackedAreaTile } from '../../components/sales/StackedAreaTile';
import { ReconciliationTile } from '../../components/sales/ReconciliationTile';
import { toast } from 'sonner';
import { ChevronRight } from 'lucide-react';

// Sales Rearview — time-series-first small-multiples grid, plus stacked-area
// mixes and reconciliation-over-time. The prior stack of monthly cards is
// demoted into a collapsed accordion at the bottom. Every metric is a line;
// no bare numbers as primary treatment. History starts Jan 2026; no YoY.
// (See /app/backend/sales_timeseries.py for payload structure.)

// Canonical scorecard order — matches the CEO review pattern and the sheet's
// own row order.
const METRIC_ORDER = [
  'revenue', 'sales_count', 'calls_booked', 'calls_held', 'no_show_rate',
  'qual_calls_had', 'qualification_rate', 'qual_calls_won', 'close_rate',
  'avg_deal_price',
];

// A collapsed month-detail card — kept for teams who still want the raw
// side-by-side Cash/Cohort view. Not the primary treatment.
function MonthDetailRow({ month }) {
  const [open, setOpen] = useState(false);
  const cash = month.cash || {};
  const cohort = month.cohort || {};

  return (
    <li className="border-t border-indie-ink/10 first:border-t-0" data-testid={`month-detail-${month.month}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-2 text-left hover:bg-indie-cream/60 px-3"
        data-testid={`month-detail-toggle-${month.month}`}
      >
        <div className="flex items-center gap-3">
          <ChevronRight size={14} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
          <span className="text-sm font-medium text-indie-ink">{month.label}</span>
          <MonthStatusBadge status={month.status} testId={`month-status-${month.month}`} />
        </div>
        <span className="text-xs text-indie-ink/70 num-tabular">
          {money(cash.revenue_bhag ?? cash.revenue_sales_sheet)} · {cash.sales_count ?? '—'} sales
        </span>
      </button>
      {open && (
        <div className="px-3 pb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border border-indie-cobalt/30 bg-indie-cobalt/5 p-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">Cash · payment date</div>
            <div className="mt-2 text-xs text-indie-ink/80 space-y-0.5">
              <div>BHAG: {money(cash.revenue_bhag)}</div>
              <div>Sheet: {money(cash.revenue_sales_sheet)}</div>
              <div>QBO: {money(cash.revenue_qbo)}</div>
              <div>Sales: {cash.sales_count ?? '—'} · Avg: {money(cash.avg_price_point)}</div>
            </div>
          </div>
          <div className="border border-indie-yellow/50 bg-indie-yellow/10 p-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">Cohort · consult date</div>
            <div className="mt-2 text-xs text-indie-ink/80 space-y-0.5">
              <div>Booked: {cohort.calls_booked ?? '—'} · Had: {cohort.qualified_calls ?? '—'} · Won: {cohort.won ?? '—'}</div>
              <div>Close: {cohort.close_rate != null ? `${(cohort.close_rate * 100).toFixed(1)}%` : '—'}</div>
              <div>Qualification rate (qual/booked): {cohort.show_up_rate != null ? `${(cohort.show_up_rate * 100).toFixed(1)}%` : '—'}</div>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

export default function SalesRearview() {
  const [ts, setTs] = useState(null);
  const [rv, setRv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/sales/timeseries'),
      api.get('/sales/rearview'),
    ])
      .then(([t, r]) => { setTs(t.data); setRv(r.data); })
      .catch(() => toast.error('Could not load Sales Rearview.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="label-editorial" data-testid="rearview-loading">Loading Sales Rearview…</div>;
  if (!ts || !rv) return <div className="label-editorial">No rearview data.</div>;

  const metricsByKey = Object.fromEntries((ts.metrics || []).map((m) => [m.key, m]));
  const orderedMetrics = METRIC_ORDER.map((k) => metricsByKey[k]).filter(Boolean);
  const perCloser = ts.per_closer || [];
  const asOf = ts.last_synced
    ? new Date(ts.last_synced).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="space-y-8" data-testid="sales-rearview-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <SectionHeader
          eyebrow="Sales · Rearview"
          title="Closed months on one page"
          description={`Every metric is a line. Dashed = plan · solid = actual. Rate charts pinned to 0–100% for month-comparability. Closed months only — cutoff at ${ts.last_closed_month || 'last complete calendar month'}. History starts January 2026; YoY comparisons available January 2027.`}
        />
        <DataSourceBadge
          source="BHAG monthly · Sales Snapshot · Time-series"
          asOf={asOf}
          confidence="green"
          testId="rearview-source-badge"
        />
      </div>

      {/* Small-multiples grid — 10 line-chart metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="rearview-small-multiples">
        {orderedMetrics.map((m) => (
          <TimeSeriesTile
            key={m.key}
            metric={m}
            methodologyBreaks={ts.methodology_breaks}
            color="#141B4D"
            goalColor="#1D57A5"
            testId={`ts-${m.key}`}
          />
        ))}
      </div>

      {/* Product mix (HubSpot dealtype) — 100% stacked area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="rearview-mix-grid">
        <StackedAreaTile
          label="Product mix (HubSpot dealtype)"
          series={ts.product_mix_monthly || []}
          formula="closed-won deals grouped by dealtype"
          source="HubSpot · Deals · property dealtype"
          methodologyBreaks={ts.methodology_breaks}
          chartId="product_mix"
          testId="rearview-product-mix"
        />
        <StackedAreaTile
          label="Sales sources"
          series={(ts.sales_sources_mix || {}).series || []}
          formula="closed-won sales grouped by Sales Snapshot source"
          source="Sales Snapshot 2026 · Sales Sources block"
          methodologyBreaks={ts.methodology_breaks}
          chartId="sales_sources"
          testId="rearview-sales-sources"
        />
        <StackedAreaTile
          label="Lead sources (qualified calls had)"
          series={(ts.lead_sources_mix || {}).series || []}
          formula="qualified consults grouped by Sales Snapshot source"
          source="Sales Snapshot 2026 · Qual Calls Had Sources block"
          methodologyBreaks={ts.methodology_breaks}
          chartId="lead_sources"
          testId="rearview-lead-sources"
        />
        <StackedAreaTile
          label="Unqualified reasons"
          series={(ts.unqualified_reasons_mix || {}).series || []}
          formula="unqualified calls grouped by Reason (per Sales Snapshot monthly tab)"
          source="Sales Snapshot 2026 · Unqualified Calls block"
          clock="cohort"
          methodologyBreaks={ts.methodology_breaks}
          chartId="unqualified_reasons_mix"
          testId="rearview-unqualified-reasons"
        />
        <StackedAreaTile
          label="Qualified stage mix"
          series={(ts.qualified_stage_mix || {}).series || []}
          formula="qualified calls grouped by 5-stage buyer intent (per Sales Snapshot monthly tab)"
          source="Sales Snapshot 2026 · Qualified Calls block"
          clock="cohort"
          methodologyBreaks={ts.methodology_breaks}
          chartId="qualified_stage_mix"
          testId="rearview-qualified-stage"
        />
      </div>

      {/* Reconciliation over time */}
      {ts.reconciliation?.length > 0 && (
        <div>
          <div className="mb-3 flex items-center justify-between">
            <div className="label-editorial">Reconciliation over time</div>
            <div className="text-[10px] text-indie-ink/50 uppercase tracking-[0.14em]">
              Percent delta by month · ±2% band = acceptable
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="rearview-reconciliation">
            {ts.reconciliation.map((pair) => (
              <ReconciliationTile key={pair.key} pair={pair} testId={`recon-${pair.key}`} />
            ))}
          </div>
        </div>
      )}

      {/* Per-closer cumulative revenue */}
      <div data-testid="rearview-per-closer-charts">
        <div className="mb-3 label-editorial">Per-closer trend</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <ClosedSplitTimeSeriesTile
            label="Revenue by closer"
            perCloser={perCloser}
            seriesKey="revenue"
            unit="usd"
            formula="Sales Snapshot Individual Performance · Rev Generated"
            source="Sales Snapshot 2026 · Individual Performance"
            testId="rearview-closer-revenue"
          />
          <ClosedSplitTimeSeriesTile
            label="Close rate by closer"
            perCloser={perCloser}
            seriesKey="close_rate"
            unit="rate"
            formula="Qual Calls Won / Qual Calls Had — per rep"
            source="Sales Snapshot 2026 · Individual Performance"
            clock="cohort"
            testId="rearview-closer-close-rate"
          />
        </div>
      </div>

      {/* Hidden panels notice */}
      {ts.hidden_panels?.length > 0 && (
        <div className="border border-dashed border-indie-ink/25 bg-indie-cream/50 p-4" data-testid="rearview-hidden-panels">
          <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold mb-2">
            Panels intentionally hidden ({ts.hidden_panels.length})
          </div>
          <ul className="text-xs text-indie-ink/70 space-y-1">
            {ts.hidden_panels.map((p) => (
              <li key={p.panel} data-testid={`hidden-panel-${p.panel}`}>
                <span className="font-mono text-indie-ink/50">{p.panel}</span> · {p.reason}
              </li>
            ))}
          </ul>
          <div className="mt-2 text-[10px] text-indie-ink/50">
            These are tracked on Sales · Integrity so gaps are visible even when the chart is not rendered.
          </div>
        </div>
      )}

      {/* Month detail — demoted accordion. Closed months only per the
          Rearview mandate; current-month tentatives live on Console. */}
      <div className="border border-indie-ink/15 bg-white" data-testid="rearview-month-detail-accordion">
        <div className="px-4 py-3 border-b border-indie-ink/10 text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">
          Month detail (raw cash / cohort tiles) · closed months only
        </div>
        <ul>
          {[...(rv.months || []).filter((m) => m.status !== 'tentative')].reverse().map((m) => (
            <MonthDetailRow key={m.month} month={m} />
          ))}
        </ul>
      </div>

      <div className="text-[11px] text-indie-ink/50 pt-2 border-t border-indie-ink/10">
        History window: {ts.history_starts} → present · YoY available from {ts.yoy_available_from}.
        Methodology breaks: {(ts.methodology_breaks || []).map((b) => `${b.date} (${b.label})`).join(' · ')}.
      </div>
    </div>
  );
}
